import { useEffect, useState } from 'react';
import { Loader2, UserCog, MessageCircle, Headphones, ShoppingCart } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { Avatar } from '../../../components/Avatar';
import { atendimentoApi } from '../lib/api';
import type { Agente, Inbox } from '../lib/tipos';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

const DEPTS: Array<{ key: Inbox; label: string; Icon: typeof Headphones }> = [
  { key: 'atendimento', label: 'Atendimento', Icon: Headphones },
  { key: 'compras', label: 'Compras', Icon: ShoppingCart },
];

export function ModalAgentes({ aberto, onFechar }: Props) {
  const toast = useToast();
  const [carregando, setCarregando] = useState(false);
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [salvandoId, setSalvandoId] = useState<number | null>(null);

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setCarregando(true);
    atendimentoApi.listarAgentes()
      .then((lista) => { if (!cancelado) setAgentes(lista); })
      .catch(() => toast.erro('Falha ao carregar agentes'))
      .finally(() => !cancelado && setCarregando(false));
    return () => { cancelado = true; };
  }, [aberto, toast]);

  async function salvar(agente: Agente, patch: Partial<Pick<Agente, 'departamentos' | 'ausente_temporario' | 'recebe_chamados'>>) {
    setSalvandoId(agente.id);
    try {
      const atualizado = await atendimentoApi.atualizarAgente(agente.id, patch);
      setAgentes((prev) => prev.map((a) => (a.id === agente.id ? { ...a, ...atualizado } : a)));
    } catch (e: any) {
      toast.erro(e?.message || 'Falha ao salvar');
    } finally {
      setSalvandoId(null);
    }
  }

  function alternarDept(agente: Agente, dept: Inbox) {
    const tem = (agente.departamentos ?? []).includes(dept);
    const novos = tem ? agente.departamentos.filter((d) => d !== dept) : [...(agente.departamentos ?? []), dept];
    salvar(agente, { departamentos: novos });
  }

  function alternarAusencia(agente: Agente) {
    salvar(agente, { ausente_temporario: !agente.ausente_temporario });
  }

  // Override do admin sobre o interruptor que e da PESSOA (engrenagem do rodape do Atendimento).
  // Existe pra consertar quem foi embora esquecendo o rodizio ligado/desligado, sem depender dela
  // voltar na tela.
  function alternarRodizio(agente: Agente) {
    salvar(agente, { recebe_chamados: !agente.recebe_chamados });
  }

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Agentes do atendimento" largura={720}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      <div className="mb-3 text-xs opacity-70 flex items-start gap-2">
        <UserCog size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          Marque os departamentos que cada um atende. So agentes em ponto entram na pool de roteamento auto.
          Duas coisas diferentes tiram alguem do rodizio: <b>Ausente</b> = nao esta na loja (ferias, atestado) — quem marca e voce;
          <b> Fora do rodizio</b> = esta na loja mas nao esta no balcao — quem marca e a propria pessoa, na engrenagem do rodape do Atendimento.
        </span>
      </div>

      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {!carregando && agentes.length === 0 && (
        <div className="text-center py-8 opacity-70 text-sm">Nenhum agente encontrado.</div>
      )}

      {!carregando && agentes.length > 0 && (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {agentes.map((a) => {
            // O laranja so grita pra quem ESTARIA no rodizio se nao fosse a chave. Usuario tecnico
            // (Balcao - Tela Pix, Caixa) e inativo nunca entraram — pintar a linha deles de laranja
            // seria alarme cobrando o que nao e pendencia, e alarme que soa a toa ensina a ignorar
            // o que e' de verdade. O rotulo continua dizendo o estado real, so sem o peso.
            const rodizioImporta = a.ativo && !a.ausente_temporario && (a.departamentos ?? []).length > 0;
            const destacarFora = rodizioImporta && !a.recebe_chamados;
            return (
            <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
              <Avatar nome={a.nome} fotoUrl={a.foto} tamanho={36} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.nome}</div>
                <div className="text-xs opacity-70 truncate">{a.login} {a.perfil !== 'vendedor' && <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-600">{a.perfil}</span>} {!a.ativo && <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-gray-500/15 text-gray-500">inativo</span>}</div>
              </div>

              <div className="flex items-center gap-1.5">
                {DEPTS.map(({ key, label, Icon }) => {
                  const ativo = (a.departamentos ?? []).includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => alternarDept(a, key)}
                      disabled={salvandoId === a.id || !a.ativo}
                      title={`${ativo ? 'Remover de' : 'Adicionar a'} ${label}`}
                      className="px-2 py-1 rounded text-xs font-medium border flex items-center gap-1 transition disabled:opacity-50"
                      style={{
                        backgroundColor: ativo ? '#fde68a' : 'transparent',
                        color: ativo ? '#000' : 'var(--page-fg, currentColor)',
                        borderColor: ativo ? '#fbbf24' : 'var(--border, rgba(0,0,0,0.15))',
                      }}
                    >
                      <Icon size={11} /> {label}
                    </button>
                  );
                })}
              </div>

              {/* DUAS chaves distintas, e a tela precisa dizer QUAL delas esta segurando —
                  senao "por que fulano nao recebeu chamado?" nao tem resposta aqui.
                  A esquerda: esta na loja? (do admin). A direita: esta no balcao? (da pessoa). */}
              <button
                onClick={() => alternarAusencia(a)}
                disabled={salvandoId === a.id || !a.ativo}
                title={a.ausente_temporario ? 'Marcar que voltou a loja' : 'Marcar ausencia (ferias, atestado, folga)'}
                className="px-2 py-1 rounded text-xs font-medium border disabled:opacity-50"
                style={{
                  backgroundColor: a.ausente_temporario ? '#fee2e2' : 'transparent',
                  color: a.ausente_temporario ? '#991b1b' : 'var(--page-fg, currentColor)',
                  borderColor: a.ausente_temporario ? '#fca5a5' : 'var(--border, rgba(0,0,0,0.15))',
                }}
              >
                {a.ausente_temporario ? 'Ausente' : 'Na loja'}
              </button>

              {/* Laranja SOLIDO com letra preta: nesta grade o amarelo ja significa "departamento
                  marcado" (botoes ao lado), entao o destaque padrao colidiria — a cor que sobra
                  livre aqui e o laranja de atencao. Nao e alarme: estar fora do rodizio e legitimo. */}
              <button
                onClick={() => alternarRodizio(a)}
                disabled={salvandoId === a.id || !a.ativo}
                title={a.recebe_chamados
                  ? 'Tirar do rodizio (normalmente quem faz isso e a propria pessoa, na engrenagem do rodape)'
                  : 'Colocar de volta no rodizio de chamados'}
                className="px-2 py-1 rounded text-xs font-medium border disabled:opacity-50 whitespace-nowrap"
                style={{
                  backgroundColor: destacarFora ? '#f97316' : 'transparent',
                  color: destacarFora ? '#000' : 'var(--page-fg, currentColor)',
                  borderColor: destacarFora ? '#c2410c' : 'var(--border, rgba(0,0,0,0.15))',
                  opacity: rodizioImporta ? 1 : 0.55,
                }}
              >
                {a.recebe_chamados ? 'No rodizio' : 'Fora do rodizio'}
              </button>

              {a.contato_jid ? (
                <span title={`Vinculado a contato ${a.contato_jid}`} className="text-xs opacity-60 flex items-center gap-1">
                  <MessageCircle size={12} className="text-green-600" />
                </span>
              ) : (
                <span title="Sem contato vinculado (nao recebe roteamento auto via WhatsApp)" className="text-xs opacity-30 flex items-center gap-1">
                  <MessageCircle size={12} />
                </span>
              )}
            </div>
            );
          })}
        </div>
      )}

      <div className="text-[11px] opacity-60 mt-3 leading-relaxed">
        <b>Dica:</b> agentes sem contato vinculado ({DEPTS.map((d) => d.label).join(' / ')}) ainda atendem, mas nao aparecem na pool automatica. Vinculo a contato e feito por SQL (sera visual na proxima onda).
      </div>
    </Modal>
  );
}
